'use client'

import dynamic from 'next/dynamic'
import {useCallback, useEffect, useRef, useState} from 'react'
import type {ComponentPropsWithoutRef} from 'react'
import type {JotulWidgetHandle, JotulWidgetProps} from '@jotul/jotul-widgets'
import type {SiteLite} from '@/multisite/types'
import {
  resolveDealerWidgetMarkets,
  resolveWidgetBrands,
} from '@/components/layout/dealerWidgetBrands'
import {campaignDealerWidgetConfig} from '@/lib/dealerWidget'

const JotulWidget = dynamic<JotulWidgetProps>(
  () => import('@jotul/jotul-widgets').then((mod) => mod.JotulWidget),
  {ssr: false},
)

// JotulWidget renders its closed drawer with `aria-hidden="true"` but leaves
// inner focusable elements (close button etc) in the tab order — Lighthouse
// flags this as `aria-hidden-focus`. The widget package owns the markup, so
// we mirror the aria-hidden state to the native `inert` attribute on each
// `.jwi-fixed` container. `inert` makes the subtree non-focusable, untouchable
// by pointer, and hidden from assistive tech in one shot.
const useMirrorWidgetAriaHiddenToInert = () => {
  useEffect(() => {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return

    const apply = (element: Element) => {
      const hidden = element.getAttribute('aria-hidden') === 'true'
      if (hidden) {
        element.setAttribute('inert', '')
      } else {
        element.removeAttribute('inert')
      }
    }

    const initialPass = () => {
      document.querySelectorAll('.jwi-fixed').forEach(apply)
    }
    initialPass()

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          if (mutation.target instanceof Element && mutation.target.matches('.jwi-fixed')) {
            apply(mutation.target)
          }
        } else if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element) {
              if (node.matches?.('.jwi-fixed')) apply(node)
              node.querySelectorAll?.('.jwi-fixed').forEach(apply)
            }
          })
        }
      }
    })

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden'],
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [])
}

type DealerDrawerTriggerProps = {
  site?: SiteLite | null
  /** Restrict dealer searches to the participating dealers for this campaign. */
  campaignSlug?: string | null
  /** Page-level brand context (e.g. on a brand page) — overrides the site brand keys. */
  brandKeys?: string[] | null
  label: string
  className: string
  autoOpen?: boolean
  analytics?: {
    name: string
    location: string
    variant: string
  }
}

const resolveWidgetLocale = (locale?: string) => {
  const value = String(locale || '').trim()
  return value || 'nb-NO'
}

const hasDealerIdDeepLink = () => {
  if (typeof window === 'undefined') return false
  return Boolean(new URLSearchParams(window.location.search).get('dealer_id')?.trim())
}

function DealerDrawerButton({
  label,
  className,
  analytics,
  isLoading,
  ...buttonProps
}: Omit<ComponentPropsWithoutRef<'button'>, 'disabled'> & {
  label: string
  className: string
  analytics?: DealerDrawerTriggerProps['analytics']
  isLoading?: boolean
}) {
  return (
    <button
      {...buttonProps}
      type="button"
      disabled={isLoading}
      className={className}
      data-analytics-cta-name={analytics?.name}
      data-analytics-cta-location={analytics?.location}
      data-analytics-cta-text={label}
      data-analytics-cta-variant={analytics?.variant}
    >
      {label}
    </button>
  )
}

export function DealerDrawerTrigger({
  site,
  campaignSlug,
  brandKeys,
  label,
  className,
  autoOpen,
  analytics,
}: DealerDrawerTriggerProps) {
  useMirrorWidgetAriaHiddenToInert()
  const widgetHandleRef = useRef<JotulWidgetHandle | null>(null)
  const didAutoOpen = useRef(false)
  const [dealerIdDeepLink, setDealerIdDeepLink] = useState(hasDealerIdDeepLink)

  useEffect(() => {
    const syncFromUrl = () => setDealerIdDeepLink(hasDealerIdDeepLink())

    syncFromUrl()
    window.addEventListener('popstate', syncFromUrl)
    return () => window.removeEventListener('popstate', syncFromUrl)
  }, [])

  useEffect(() => {
    if (!autoOpen || dealerIdDeepLink !== false || didAutoOpen.current) return

    const handle = widgetHandleRef.current
    if (!handle) return

    didAutoOpen.current = true
    handle.open()
  }, [autoOpen, dealerIdDeepLink])

  const handleWidgetRef = useCallback(
    (handle: JotulWidgetHandle | null) => {
      widgetHandleRef.current = handle
      if (!autoOpen || dealerIdDeepLink !== false || !handle || didAutoOpen.current) return

      didAutoOpen.current = true
      handle.open()
    },
    [autoOpen, dealerIdDeepLink],
  )

  const widgetLocale = dealerIdDeepLink ? undefined : resolveWidgetLocale(site?.locale)
  const widgetMarkets = dealerIdDeepLink ? undefined : resolveDealerWidgetMarkets(site)
  const campaignWidget = campaignDealerWidgetConfig(campaignSlug)

  return (
    <JotulWidget
      type={campaignWidget.type}
      className="dealer-drawer-widget !w-auto !max-w-none !text-left [&_*]:!text-left [&>.jwi-flex]:!py-0"
      locale={widgetLocale}
      markets={widgetMarkets}
      brands={resolveWidgetBrands(brandKeys, site)}
      campaignSlug={campaignWidget.campaignSlug}
      widgetRef={handleWidgetRef}
      styling={{
        button: {
          borderRadius: '9999px',
          backgroundColor: '#ef2b18',
          textColor: '#ffffff',
        },
        border: {
          borderColor: '#ef2b18',
        },
      }}
      button={<DealerDrawerButton label={label} className={className} analytics={analytics} />}
      buttonLoading={
        <DealerDrawerButton label={label} className={className} analytics={analytics} isLoading />
      }
    />
  )
}
